import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Loader from "@mui/material/CircularProgress";
import toast from "react-hot-toast";

const VerifyPayment = () => {
  const API_URL = process.env.REACT_APP_API_URL
  const queryClient = useQueryClient()
  const [utr, setUtr] = useState("");
  const [receipt, setReceipt] = useState(null);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const navigate = useNavigate();
  const {data: authUser} = useQuery({queryKey: ['authUser']})
  const {mutate, isPending} = useMutation({
    mutationFn: async ({paymentDetails, receipt}) => {
      const formData = new FormData()
      formData.append("receipt", receipt)
      formData.append("paymentDetails", JSON.stringify(paymentDetails))

      const res = await fetch(`${API_URL}/registration/upload-payment-details`,{
        method: "POST",
        credentials: "include",
        body: formData
      })      
      const data = await res.json()

      if(!res.ok) {
        throw new Error(data.error || "Something went wrong")
      }
      return data;
    },
    onSuccess: (data) => {
      toast.success(data.message)
      queryClient.invalidateQueries({queryKey: ['authUser']})
      navigate("/conference-registration/dashboard")
    },
    onError: (err) => {
      toast.error(err.message)
    }
  })
  useEffect(() => {
    // Fetch payment details from sessionStorage when the component mounts
    const savedDetails = JSON.parse(sessionStorage.getItem("paymentDetails"));
    console.log(savedDetails);
    
    if(!savedDetails) {
      navigate("/conference-registration/fee-payment")
    }
    setPaymentDetails(savedDetails);    
  }, [navigate]);

  const handleSubmit = (e) => {
    e.preventDefault();   

    // Combine UTR and Receipt with existing payment details
    const updatedDetails = {
      ...paymentDetails,
      utr,
    };
    mutate({paymentDetails: updatedDetails, receipt})
  };
if(!paymentDetails) return null;
if(authUser?.amountPaid) {
  navigate("/conference-registration/payment")
}
  return (
    <div className="max-w-3xl mx-auto p-6 bg-white">
      <h1 className="text-2xl font-medium text-gray-800 mb-4">
        Payment Details for IMMUNCON2025
      </h1>

      {/* Instructional Message */}
      <div className="p-4 mb-6 bg-yellow-100 text-yellow-800 rounded-lg">
        <h2 className="text-lg font-semibold">Important Instructions:</h2>
        <p className="mt-2">
          Please make the payment of{" "}
          <strong>{paymentDetails.registrationCategory.includes("outside India") ? "$" : "₹"}{paymentDetails.amount || "N/A"} </strong>
          using the bank account details provided below. Once the payment is
          made, upload the payment receipt and enter the UTR (Unique Transaction
          Reference) number to complete your registration.
        </p>
      </div>

      {/* Display Selected Payment Details */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-700">
          Selected Options
        </h2>
        <ul className="list-disc list-inside text-gray-600 mt-2">
          <li>
            <strong>Amount:</strong> {paymentDetails.registrationCategory.includes("outside India") ? "$" : "₹"}{paymentDetails.amount || "N/A"}
          </li>
          <li>
            <strong>Conference Category:</strong>{" "}
            {paymentDetails.conferenceCategory || "N/A"}
          </li>
          <li>
            <strong>Membership Number:</strong>{" "}
            {paymentDetails.membershipNumber || "N/A"}
          </li>
          <li>
            <strong>Registration Category:</strong>{" "}
            {paymentDetails.registrationCategory || "N/A"}
          </li>
        </ul>
      </div>

      {/* Bank Account Details */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-700">
          Bank Account Details
        </h2>
        <ul className="list-disc list-inside text-gray-600 mt-2">
          <li>
            <strong>Account Name:</strong> SNFCE MNNIT Allahabad
          </li>
          <li>
            <strong>Account Number:</strong> 10424975574
          </li>
          <li>
            <strong>IFSC Code:</strong> SBIN0002580
          </li>
          <li>
            <strong>SWIFT Code:</strong> SBININBB828
          </li>
          <li>
            <strong>Bank:</strong> State Bank of India
          </li>
          <li>
            <strong>Branch Name:</strong> MNNIT Allahabad
          </li>
        </ul>
        <p className="mt-4 text-gray-700">
          <strong>*Important:</strong> Please mention <em>IMMUNCON2025</em> in
          the remarks while making the payment.
        </p>
      </div>

      <div className="border-t border-gray-200 my-6"></div>

      {/* UTR and Receipt Upload Form */}
      <form onSubmit={handleSubmit}>
        <h2 className="text-lg font-semibold text-gray-700 mb-4">
          Upload Payment Receipt
        </h2>
        <div className="mb-4">
          <label
            htmlFor="utr"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            UTR Number
          </label>
          <input
            type="text"
            id="utr"
            value={utr}
            onChange={(e) => setUtr(e.target.value)}
            className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-[#4f46e5]"
            placeholder="Enter UTR Number"
            required
          />
        </div>
        <div className="mb-4">
          <label
            htmlFor="receipt"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Upload Receipt
          </label>
          <input
            type="file"
            id="receipt"
            accept=".jpg,.jpeg,.png,.pdf"
            onChange={(e) => setReceipt(e.target.files[0])}
            className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-[#4f46e5]"
            required
          />
        </div>
        <button
          type="submit"
          className="px-6 py-2 bg-[#4f46e5] text-white rounded-lg hover:bg-[#6a63e9]"
        >
          {isPending ?  <Loader sx={() => ({ color: "#ffffff" })} size={24}/> : "Submit"}
        </button>
      </form>
    </div>
  );
};

export default VerifyPayment;